import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../store/AuthContext'
import { useToast } from '../../components/Toast'
import employeesService from '../../services/employeesService'
import { companiesService, MonthlyEntry } from '../../services/companiesService'
import { ConfirmModal } from '../../components/ConfirmModal'
import { useState } from 'react'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts'

const maturityLabels: Record<string, string> = {
  startup: 'Startup',
  traction: 'Tração',
  growth: 'Growth',
  scale: 'Scale',
  mature: 'Madura',
}

const maturityColors: Record<string, string> = {
  startup: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  traction: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  growth: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  scale: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  mature: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
}

const monthShort = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

/** Parse "YYYY-MM-DD" without timezone shift */
function parseDate(s: string) {
  const [y, m, d] = s.split('-').map(Number)
  return { year: y, month: m, day: d }
}

function fmt(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return 'R$ 0'
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

export const CompanyDashboardPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const companyId = Number(id)
  const isAdmin = user?.role === 'admin'

  const [deleteTarget, setDeleteTarget] = useState<MonthlyEntry | null>(null)
  const [entriesYear, setEntriesYear] = useState(new Date().getFullYear())
  const [editTarget, setEditTarget] = useState<MonthlyEntry | null>(null)
  const [editMonth, setEditMonth] = useState('')
  const [editYear, setEditYear] = useState('')
  const [editMrr, setEditMrr] = useState('')
  const [editMargin, setEditMargin] = useState('')
  const [editError, setEditError] = useState('')

  // Check if this is the user's own company
  const { data: allEmployees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeesService.getAll(),
    staleTime: 60_000,
  })
  const myEmployee = allEmployees.find((e) => e.user_detail?.id === user?.id)
  const isMyCompany = myEmployee?.primary_company_detail?.id === companyId

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['company-dashboard', companyId],
    queryFn: () => companiesService.getDashboard(companyId),
    enabled: !!companyId,
  })

  const deleteMutation = useMutation({
    mutationFn: (entryId: number) => companiesService.deleteMonthlyEntry(companyId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-dashboard', companyId] })
      toast.success('Lançamento excluído. Trimestre recalculado.')
      setDeleteTarget(null)
    },
  })

  const recalculateMutation = useMutation({
    mutationFn: () => companiesService.recalculateQuarters(companyId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['company-dashboard', companyId] })
      queryClient.invalidateQueries({ queryKey: ['group-overview'] })
      toast.success(data.detail)
    },
    onError: () => {
      toast.error('Erro ao recalcular trimestres.')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ entryId, data }: { entryId: number; data: { reference_date?: string; net_revenue: string; net_margin: string } }) =>
      companiesService.updateMonthlyEntry(companyId, entryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-dashboard', companyId] })
      queryClient.invalidateQueries({ queryKey: ['group-overview'] })
      toast.success('Lançamento atualizado. Trimestre recalculado.')
      setEditTarget(null)
    },
    onError: (error: any) => {
      const d = error.response?.data
      const msg = d?.detail
        || d?.reference_date?.[0]
        || d?.non_field_errors?.[0]
        || d?.net_revenue?.[0]
        || d?.net_margin?.[0]
        || (typeof d === 'string' ? d : JSON.stringify(d))
        || 'Erro ao atualizar.'
      setEditError(msg)
    },
  })

  const openEdit = (entry: MonthlyEntry) => {
    const d = parseDate(entry.reference_date)
    setEditTarget(entry)
    setEditMonth(String(d.month))
    setEditYear(String(d.year))
    setEditMrr(entry.net_revenue)
    setEditMargin(entry.net_margin)
    setEditError('')
  }

  const checkEditDuplicate = async (month: string, year: string) => {
    if (!editTarget) return
    try {
      const entries = await companiesService.getMonthlyEntries(companyId)
      const refDate = `${year}-${month.padStart(2, '0')}-01`
      const duplicate = entries.find(
        (e) => e.reference_date === refDate && e.id !== editTarget.id
      )
      if (duplicate) {
        toast.warning(`Já existe um lançamento para ${monthShort[Number(month)]}/${year}.`)
      }
    } catch {
      // silently ignore
    }
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editTarget) return
    const mrr = parseFloat(editMrr)
    const margin = parseFloat(editMargin)
    if (isNaN(mrr) || mrr <= 0) { setEditError('MRR deve ser maior que zero.'); return }
    if (isNaN(margin)) { setEditError('Lucratividade é obrigatória.'); return }

    const refDate = `${editYear}-${editMonth.padStart(2, '0')}-01`
    updateMutation.mutate({
      entryId: editTarget.id,
      data: { reference_date: refDate, net_revenue: mrr.toFixed(2), net_margin: margin.toFixed(2) },
    })
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Carregando dashboard...</p>
      </div>
    )
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">Empresa não encontrada.</p>
        <button onClick={() => navigate('/empresas')} className="mt-4 text-primary hover:underline">
          Voltar para Empresas
        </button>
      </div>
    )
  }

  const { company, thresholds, graduation, downgrade, current_quarter_status, monthly_entries, quarterly_results } = dashboard
  const mrrMin = parseFloat(thresholds.mrr_min)
  const marginMin = parseFloat(thresholds.margin_min)

  // Last closed quarter (3 months complete) for summary cards
  const lastClosedQ = quarterly_results.find((r) => r.months_reported === 3)
  const lastClosedLabel = lastClosedQ ? `Q${lastClosedQ.quarter}/${lastClosedQ.year}` : null

  // Entries filtered by year + available years for pagination
  const availableYears = [...new Set(
    monthly_entries.map((e) => parseDate(e.reference_date).year)
  )].sort((a, b) => b - a)

  const filteredEntries = monthly_entries.filter(
    (e) => parseDate(e.reference_date).year === entriesYear
  )

  // Quarter status
  const qStatus = current_quarter_status
  const currentQ = Math.ceil((new Date().getMonth() + 1) / 3)

  // Chart data (last 12 months only, reversed to chronological)
  const chartData = [...monthly_entries].slice(0, 12).reverse().map((e) => {
    const d = parseDate(e.reference_date)
    return {
      label: `${monthShort[d.month]}/${String(d.year).slice(2)}`,
      mrr: parseFloat(e.net_revenue),
      margin: parseFloat(e.net_margin),
    }
  })

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {isAdmin && (
            <button
              onClick={() => navigate('/empresas')}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary mb-1 flex items-center gap-1"
            >
              ← Empresas
            </button>
          )}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {company.name}
          </h1>
          {isMyCompany && (
            <p className="text-sm text-primary font-medium mt-1">Esta é a empresa a qual você pertence</p>
          )}
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${maturityColors[company.maturity_stage]}`}>
          {maturityLabels[company.maturity_stage]}
        </span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* MRR */}
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-5">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            Faturamento Médio {lastClosedLabel || '—'}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {lastClosedQ ? fmt(lastClosedQ.average_mrr) : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {mrrMin === 0 ? 'Sem mínimo de faturamento' : `Min: ${fmt(mrrMin)}`}
          </p>
        </div>

        {/* Margin */}
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-5">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            Lucratividade Média {lastClosedLabel || '—'}
          </p>
          <p className={`text-2xl font-bold ${
            lastClosedQ && parseFloat(lastClosedQ.average_margin) >= 0
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            {lastClosedQ ? `${lastClosedQ.average_margin}%` : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {`Min: ${marginMin}%`}
          </p>
        </div>

        {/* Months reported */}
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-5">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Meses Q{currentQ}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {qStatus ? `${qStatus.months_reported} de 3` : '0 de 3'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Lançamentos no trimestre</p>
        </div>

        {/* Status */}
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-5">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Distribuição Q{currentQ}</p>
          {qStatus ? (
            qStatus.is_eligible ? (
              <p className="text-xl font-bold text-green-600 dark:text-green-400">Elegível ✓</p>
            ) : (
              <p className="text-xl font-bold text-red-600 dark:text-red-400">Inativa ✗</p>
            )
          ) : (
            <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">Aguardando</p>
          )}
          {qStatus?.suspension_reason && (
            <p className="text-xs text-red-500 mt-1 line-clamp-2">{qStatus.suspension_reason}</p>
          )}
        </div>
      </div>

      {/* Graduation Banner */}
      {graduation.next_stage && (
        <div className={`rounded-card shadow p-4 mb-6 ${
          graduation.qualifies
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`font-semibold ${graduation.qualifies ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                {graduation.qualifies
                  ? `Empresa apta a graduar para ${maturityLabels[graduation.next_stage]}!`
                  : `Graduação para ${maturityLabels[graduation.next_stage]}`
                }
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                MRR {'>='} {graduation.graduation_mrr ? fmt(graduation.graduation_mrr) : '—'} por{' '}
                {graduation.required_months} meses consecutivos.{' '}
                Progresso: <strong>{graduation.consecutive_months}/{graduation.required_months}</strong> meses.
                {graduation.graduation_nrr && (
                  <> {' '}<em className="text-gray-500">(também exige NRR {'>='} {graduation.graduation_nrr}%)</em></>
                )}
              </p>
            </div>
            {graduation.qualifies && (
              <span className="px-3 py-1 rounded-full text-sm font-bold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400">
                Apta
              </span>
            )}
          </div>
        </div>
      )}

      {/* Downgrade Banner */}
      {downgrade.qualifies && downgrade.previous_stage && (
        <div className="rounded-card shadow p-4 mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-red-700 dark:text-red-400">
                Risco de rebaixamento para {maturityLabels[downgrade.previous_stage]}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {downgrade.reason}
              </p>
            </div>
            <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400">
              Rebaixar
            </span>
          </div>
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Evolução — Últimos {chartData.length} meses
          </h2>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="mrr"
                orientation="left"
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                yAxisId="margin"
                orientation="right"
                unit="%"
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value, name) => {
                  const num = Number(value)
                  return name === 'MRR' ? fmt(num) : `${num.toFixed(2)}%`
                }}
              />
              <Legend />
              <Bar yAxisId="mrr" dataKey="mrr" name="MRR" fill="#2563EB" opacity={0.3} barSize={30} />
              <Line yAxisId="margin" dataKey="margin" name="Margem %" stroke="#22C55E" strokeWidth={2} dot={{ r: 3 }} />
              {mrrMin > 0 && (
                <ReferenceLine yAxisId="mrr" y={mrrMin} stroke="#EF4444" strokeDasharray="6 4" label={{ value: `MRR min ${fmt(mrrMin)}`, fill: '#EF4444', fontSize: 11, position: 'insideTopLeft' }} />
              )}
              {marginMin > 0 && (
                <ReferenceLine yAxisId="margin" y={marginMin} stroke="#F59E0B" strokeDasharray="6 4" label={{ value: `Margem min ${marginMin}%`, fill: '#F59E0B', fontSize: 11, position: 'insideBottomRight' }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Quarterly History Table */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Histórico Trimestral</h2>
          {isAdmin && (
            <button
              onClick={() => recalculateMutation.mutate()}
              disabled={recalculateMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              title="Recalcular todos os trimestres e remover órfãos"
            >
              {recalculateMutation.isPending ? (
                <>
                  <span className="animate-spin inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full" />
                  Recalculando...
                </>
              ) : (
                'Recalcular Trimestres'
              )}
            </button>
          )}
        </div>
        {quarterly_results.length === 0 ? (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">Nenhum resultado trimestral calculado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Trimestre</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">MRR Médio</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Margem Média</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Meses</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Motivo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {quarterly_results.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-3 font-medium text-gray-900 dark:text-white">Q{r.quarter}/{r.year}</td>
                    <td className="px-6 py-3 text-right text-gray-700 dark:text-gray-300">{fmt(r.average_mrr)}</td>
                    <td className="px-6 py-3 text-right text-gray-700 dark:text-gray-300">{r.average_margin}%</td>
                    <td className="px-6 py-3 text-center text-gray-700 dark:text-gray-300">{r.months_reported}/3</td>
                    <td className="px-6 py-3 text-center">
                      {r.is_eligible ? (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          Distribuiu
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                          Inativa
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate cursor-default" title={r.suspension_reason || ''}>
                      {r.suspension_reason || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monthly Entries List */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lançamentos Mensais</h2>
          {monthly_entries.length > 0 && (
            <div className="flex items-center gap-2">
              {availableYears.map((y) => (
                <button
                  key={y}
                  onClick={() => setEntriesYear(y)}
                  className={`px-3 py-1 text-sm font-medium rounded-btn transition-colors ${
                    entriesYear === y
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
        </div>
        {filteredEntries.length === 0 ? (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">
            {monthly_entries.length === 0 ? 'Nenhum lançamento registrado.' : `Nenhum lançamento em ${entriesYear}.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Competência</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">MRR</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Margem</th>
                  {isAdmin && (
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Ações</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredEntries.map((e) => {
                  const d = parseDate(e.reference_date)
                  const label = `${monthShort[d.month]}/${d.year}`
                  return (
                    <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-3 font-medium text-gray-900 dark:text-white">{label}</td>
                      <td className="px-6 py-3 text-right text-gray-700 dark:text-gray-300">{fmt(e.net_revenue)}</td>
                      <td className={`px-6 py-3 text-right font-medium ${
                        parseFloat(e.net_margin) >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {e.net_margin}%
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-3 text-right">
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => openEdit(e)}
                              className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => setDeleteTarget(e)}
                              className="px-3 py-1 text-xs border border-danger rounded-btn text-danger hover:bg-danger hover:text-white transition-colors"
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Entry Modal */}
      {editTarget && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setEditTarget(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Editar Lançamento
              </h3>

              {editError && (
                <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-btn text-danger text-sm">
                  {editError}
                </div>
              )}

              <form onSubmit={handleEditSubmit} className="space-y-4">
                {/* Month / Year */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Mês
                    </label>
                    <select
                      value={editMonth}
                      onChange={(e) => {
                        setEditMonth(e.target.value)
                        checkEditDuplicate(e.target.value, editYear)
                      }}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {monthShort.slice(1).map((name, i) => (
                        <option key={i + 1} value={i + 1}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Ano
                    </label>
                    <select
                      value={editYear}
                      onChange={(e) => {
                        setEditYear(e.target.value)
                        checkEditDuplicate(editMonth, e.target.value)
                      }}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Faturamento Mensal — MRR (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editMrr}
                    onChange={(e) => setEditMrr(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Lucratividade Alcançada (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editMargin}
                    onChange={(e) => setEditMargin(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setEditTarget(null)}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Entry Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Excluir Lançamento"
        description={
          <p>Tem certeza que deseja excluir este lançamento? O resultado trimestral será recalculado.</p>
        }
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
        isDanger
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}

export default CompanyDashboardPage
